/**
 * Dates, text fitting and the time axis for the timeline chart (timelineGeometry.js).
 * Dates are whole UTC day numbers, so no timezone can shift a bar or the today line.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { ELLIPSIS, fixed } from './chartBasics.js';

const DAY_MS = 86400000;
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MONTHS = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
const WEEKDAY = Object.freeze({ SUNDAY: 0, MONDAY: 1, SATURDAY: 6 });

/** A `YYYY-MM-DD` date as a UTC day number, or null when it is not a real calendar date. */
export function dayNumber(iso) {
  const m = typeof iso === 'string' ? ISO_DATE.exec(iso) : null;
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (date.getUTCFullYear() !== y || date.getUTCMonth() !== mo - 1 || date.getUTCDate() !== d) return null;
  return Math.round(date.getTime() / DAY_MS);
}

const dateOf = (n) => new Date(n * DAY_MS);
export const yearOf = (n) => dateOf(n).getUTCFullYear();
export const isWeekend = (n) => [WEEKDAY.SUNDAY, WEEKDAY.SATURDAY].includes(dateOf(n).getUTCDay());

/** "6 Oct", or "6 Oct 2027" with the year. */
export function formatDay(n, withYear = false) {
  const d = dateOf(n);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}${withYear ? ` ${d.getUTCFullYear()}` : ''}`;
}

/*
 * Text width without a browser: per-character widths in em for Figtree, rounded up a little so
 * a label that "fits" here really fits. The showcase measured with a canvas; node has none.
 */
const CHAR_EM = Object.freeze({ narrow: 0.28, slim: 0.36, wide: 0.82, upper: 0.64, digit: 0.56, other: 0.53 });
const NARROW_CHARS = "ijl.,:;|!'";
const SLIM_CHARS = 'frt()[]-/" ';
const WIDE_CHARS = 'mwMW@%';
const SEMIBOLD = 600;
const SEMIBOLD_FACTOR = 1.05;

function charEm(ch) {
  if (NARROW_CHARS.includes(ch)) return CHAR_EM.narrow;
  if (SLIM_CHARS.includes(ch)) return CHAR_EM.slim;
  if (WIDE_CHARS.includes(ch)) return CHAR_EM.wide;
  if (ch >= '0' && ch <= '9') return CHAR_EM.digit;
  if (ch >= 'A' && ch <= 'Z') return CHAR_EM.upper;
  return CHAR_EM.other;
}

/** Estimated width in px of `text` at `size` px and `weight`. */
export function textWidth(text, size, weight = 400) {
  const em = Array.from(String(text)).reduce((sum, ch) => sum + charEm(ch), 0);
  return em * size * (weight >= SEMIBOLD ? SEMIBOLD_FACTOR : 1);
}

/** Cuts `text` with … to fit `maxPx`; `full` keeps the whole text for a tooltip. */
export function fitText(text, maxPx, size, weight = 400) {
  const full = String(text);
  if (textWidth(full, size, weight) <= maxPx) return { text: full, full, cut: false };
  let lo = 0;
  let hi = full.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (textWidth(`${full.slice(0, mid).trimEnd()}${ELLIPSIS}`, size, weight) <= maxPx) lo = mid;
    else hi = mid - 1;
  }
  return { text: lo ? `${full.slice(0, lo).trimEnd()}${ELLIPSIS}` : '', full, cut: true };
}

/** Shortest span drawn when the range comes from the items, so one short task is not a wall. */
const MIN_SPAN_DAYS = 14;
const EDGE_PAD_SHARE = 0.03;

/**
 * Maps days to x. `range` (day numbers, inclusive) wins; otherwise the span covers today and every
 * item, with a little air on both sides.
 */
export function makeScale({ items, today, range }, left, right) {
  let lo;
  let hi;
  if (range) {
    lo = range.from;
    hi = range.to + 1;
  } else {
    lo = items.reduce((min, it) => Math.min(min, it.start === null ? it.end : it.start), today);
    hi = items.reduce((max, it) => Math.max(max, it.end + 1), today + 1);
    const pad = Math.max(1, Math.round((hi - lo) * EDGE_PAD_SHARE));
    lo -= pad;
    hi += pad;
    if (hi - lo < MIN_SPAN_DAYS) {
      const extra = MIN_SPAN_DAYS - (hi - lo);
      lo -= Math.floor(extra / 2);
      hi += Math.ceil(extra / 2);
    }
  }
  const k = (right - left) / (hi - lo);
  return { lo, hi, k, left, right, x: (n) => left + (n - lo) * k };
}

export const TICK_UNIT = Object.freeze({ DAY: 'day', WEEK: 'week', MONTH: 'month' });
/** Finest first; `min` is the px one step needs. The last one (quarters) is the fallback. */
const TICK_STEPS = Object.freeze([
  { unit: TICK_UNIT.DAY, step: 1, min: 24 },
  { unit: TICK_UNIT.DAY, step: 2, min: 28 },
  { unit: TICK_UNIT.WEEK, step: 1, min: 30 },
  { unit: TICK_UNIT.WEEK, step: 2, min: 30 },
  { unit: TICK_UNIT.MONTH, step: 1, min: 30 },
  { unit: TICK_UNIT.MONTH, step: 3, min: 30 },
]);
const STEP_DAYS = Object.freeze({ [TICK_UNIT.DAY]: 1, [TICK_UNIT.WEEK]: 7, [TICK_UNIT.MONTH]: 30.44 });
const MONTH_GUARD = 400;
export const TICK_FONT = 10;
const TICK_INSET = 3;
const TICK_GAP = 6;

function tickDays(sc, pick) {
  const days = [];
  if (pick.unit === TICK_UNIT.MONTH) {
    const d0 = dateOf(sc.lo);
    for (let i = 0, y = d0.getUTCFullYear(), m = d0.getUTCMonth(); i < MONTH_GUARD; i += 1) {
      const n = Math.round(Date.UTC(y, m, 1) / DAY_MS);
      if (n >= sc.hi) break;
      if (n >= sc.lo && m % pick.step === 0) days.push(n);
      m += 1;
      if (m > 11) { m = 0; y += 1; }
    }
  } else if (pick.unit === TICK_UNIT.WEEK) {
    let n = sc.lo;
    while (dateOf(n).getUTCDay() !== WEEKDAY.MONDAY) n += 1;
    for (let i = 0; n < sc.hi; n += 7, i += 1) if (i % pick.step === 0) days.push(n);
  } else {
    for (let n = sc.lo; n < sc.hi; n += 1) if (n % pick.step === 0) days.push(n);
  }
  return days;
}

function tickLabel(unit, n, prev) {
  const d = dateOf(n);
  const p = prev === null ? null : dateOf(prev);
  if (unit === TICK_UNIT.MONTH) {
    return !p || p.getUTCFullYear() !== d.getUTCFullYear() ? `${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}` : MONTHS[d.getUTCMonth()];
  }
  return !p || p.getUTCMonth() !== d.getUTCMonth() ? formatDay(n) : String(d.getUTCDate());
}

/** Picks days / weeks / months / quarters by real pixels per step, then drops labels that would touch. */
export function axisTicks(sc) {
  const pick = TICK_STEPS.find((o) => o.step * STEP_DAYS[o.unit] * sc.k >= o.min) || TICK_STEPS[TICK_STEPS.length - 1];
  const days = tickDays(sc, pick);
  const labels = [];
  let lastEnd = -Infinity;
  let prevShown = null;
  days.forEach((n) => {
    const x = sc.x(n) + TICK_INSET;
    const text = tickLabel(pick.unit, n, prevShown);
    const w = textWidth(text, TICK_FONT);
    if (x < lastEnd + TICK_GAP || x + w > sc.right + TICK_GAP) return;
    labels.push({ x: fixed(x), text });
    lastEnd = x + w;
    prevShown = n;
  });
  const grid = days.map((n) => fixed(sc.x(n))).filter((x) => x >= sc.left - 0.5 && x <= sc.right + 0.5);
  return { unit: pick.unit, step: pick.step, grid, labels };
}
